import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Upload, Download, Archive, RotateCcw, Loader2, AlertCircle } from 'lucide-react';

/**
 * PartnerDocuments — the "Documents" section on a partner organisation.
 * Card PARTNER-DOCUMENTS (Ryland via Daniel, 8 Oct 2026).
 *
 * Agreements, BAAs, confidentiality agreements, W-9s, licences, insurance
 * certificates — kept against the partner they belong to.
 *
 * ⛔⛔ STAFF ONLY. Partners never see this. Not because the component is not
 * shipped to them — the portal is a separate bundle — but because the five
 * channels it calls are absent from PARTNER_REACHABLE_CHANNELS, so a partner
 * session is refused at the transport. The UI is the convenience; the server
 * is the control.
 *
 * ⚠ EVERY BUTTON HERE IS ALSO GATED SERVER-SIDE on alliance_partner:admin.
 * Hiding a control is a courtesy to the user, never a security boundary — this
 * repo has a whole class of prior defects that began with a hidden button
 * standing in for a permission check.
 */

const TYPE_LABELS = {
  agreement: 'Agreement',
  baa: 'BAA / Confidentiality',
  w9: 'W-9 / Tax',
  licence: 'Licence',
  insurance: 'Insurance',
  other: 'Other',
};
/** Must equal migration 183's CHECK and the server's DOC_TYPES. A test asserts it. */
const TYPE_OPTIONS = Object.keys(TYPE_LABELS);

/**
 * ⛔ 7 MB, and it must equal database/partnerDocumentTypes.js MAX_FILE_BYTES.
 * The ceiling is set by the transport: the file travels as base64 inside the
 * JSON body of POST /api/ipc, which express caps at 10 MB globally
 * (server.js:321). A guard test couples the two numbers and the express limit.
 */
const MAX_BYTES = 7 * 1024 * 1024;
const MAX_NOTE = 500;

/** Extensions offered in the picker. The SERVER decides by magic bytes. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.heic,.docx';

function fmtSize(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtWhen(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export default function PartnerDocuments({ orgId, canManage, invoke, flashOk, flashErr, refreshNonce = 0 }) {
  const [docs, setDocs] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState(null);   // { file, docType, note }
  const [localErr, setLocalErr] = useState(null);
  const fileRef = useRef(null);

  const call = useCallback(
    (channel, payload) => (invoke || window.zignagenixAPI.invoke)(channel, payload),
    [invoke],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await call('alliance_partner:documents:list', { organizationId: orgId, archived: showArchived });
      if (r && r.error) throw new Error(r.message || r.code);
      setDocs(Array.isArray(r?.documents) ? r.documents : []);
      setTruncated(Boolean(r?.truncated));
    } catch (e) {
      flashErr(e.message || 'Could not load documents.');
      setDocs([]);
      setTruncated(false);
    } finally {
      setLoading(false);
    }
    // refreshNonce is a dependency, not a parameter: the page's Refresh button
    // increments it and this list reloads because of it.
  }, [orgId, showArchived, refreshNonce, call, flashErr]);

  useEffect(() => { load(); }, [load]);

  function chooseFile(file) {
    setLocalErr(null);
    if (!file) return;
    /*
     * ⚠ A CLIENT-SIDE SIZE CHECK, AND NOTHING ELSE.
     *
     * Size is checked here so a staff member learns a 40 MB scan is too big
     * BEFORE waiting for the upload — the server enforces the same limit and
     * is the authority. TYPE is deliberately NOT pre-judged here: the browser's
     * idea of a file's type comes from its extension, which is exactly what the
     * server refuses to trust. Showing "looks fine" here and a refusal there
     * would be worse than one honest answer from the server.
     */
    if (file.size > MAX_BYTES) {
      setLocalErr(`That file is ${fmtSize(file.size)}. The limit is ${fmtSize(MAX_BYTES)}.`);
      return;
    }
    setPending({ file, docType: 'agreement', note: '' });
  }

  async function doUpload() {
    if (!pending || busy) return;
    setBusy(true);
    setLocalErr(null);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = () => reject(new Error('That file could not be read.'));
        // result is "data:<mime>;base64,<payload>" — keep only the payload.
        fr.onload = () => resolve(String(fr.result || '').split(',')[1] || '');
        fr.readAsDataURL(pending.file);
      });

      const r = await call('alliance_partner:documents:upload', {
        organizationId: orgId,
        docType: pending.docType,
        fileName: pending.file.name,
        note: pending.note ? pending.note.slice(0, MAX_NOTE) : null,
        // Sent so the SERVER can audit a mismatch between what the browser
        // claimed and what the bytes actually are. It decides nothing.
        contentType: pending.file.type || null,
        fileDataBase64: b64,
      });
      if (r && r.error) {
        /*
         * ⛔ The transport can refuse before the server ever sees the file.
         * express caps the JSON body at 10 MB globally (server.js:321) and the
         * file travels as base64 — 4/3 of its size — so an oversized upload
         * comes back as a 413 that public/apiClient.js names PAYLOAD_TOO_LARGE.
         * This component knows its own ceiling, so it says the number.
         */
        if (r.code === 'PAYLOAD_TOO_LARGE') {
          throw new Error(`That file is too large to upload (limit ${fmtSize(MAX_BYTES)}).`);
        }
        throw new Error(r.message || r.code);
      }

      flashOk('Document saved.');
      setPending(null);
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e) {
      setLocalErr(e.message || 'That document could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function download(doc) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await call('alliance_partner:documents:get-url', { documentId: doc.id });
      if (r && r.error) throw new Error(r.message || r.code);
      /*
       * ⚠ Opened in a new tab rather than fetched and re-served. The URL is a
       * short-lived bearer token for a tax document; handing it straight to the
       * browser means it is used once, immediately, and never stored by us.
       * `noopener` so the opened tab cannot reach back into this one.
       */
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      flashErr(e.message || 'Could not open that document.');
    } finally {
      setBusy(false);
    }
  }

  async function setArchived(doc, archive) {
    if (busy) return;
    if (archive && !window.confirm(
      `Remove "${doc.fileName}" from the active list?\n\n`
      + 'The file is KEPT — it moves to Archived and can be restored. '
      + 'Nothing is deleted.',
    )) return;
    setBusy(true);
    try {
      const r = await call(
        archive ? 'alliance_partner:documents:archive' : 'alliance_partner:documents:restore',
        { documentId: doc.id },
      );
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk(archive ? 'Moved to Archived — the file is kept.' : 'Restored.');
      await load();
    } catch (e) {
      flashErr(e.message || 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const card = {
    gridColumn: '1/-1', marginTop: 14, padding: 14,
    background: '#F8F9FA', border: '1px solid #E9ECEF', borderRadius: 8,
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <FileText size={15} color="#2E8B98" />
        <strong style={{ fontSize: 13, color: '#212529' }}>Documents</strong>
        <span style={{ fontSize: 11, color: '#6C757D' }}>
          Agreements, BAAs, W-9s, licences, insurance. Staff only — partners cannot see these.
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            style={{ background: 'none', border: 'none', padding: 0, color: '#2E8B98', textDecoration: 'underline', cursor: 'pointer', fontSize: 11 }}
          >
            {showArchived ? 'Show active' : 'Show archived'}
          </button>
        </div>
      </div>

      {canManage && !showArchived && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            chooseFile(e.dataTransfer?.files?.[0]);
          }}
          style={{
            border: `1px dashed ${dragOver ? '#2E8B98' : '#CED4DA'}`,
            background: dragOver ? '#E8F4F6' : '#fff',
            borderRadius: 8, padding: 14, textAlign: 'center', marginBottom: 10,
          }}
        >
          <Upload size={16} color="#6C757D" />
          <div style={{ fontSize: 12, color: '#6C757D', margin: '6px 0' }}>
            Drag a file here, or{' '}
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              style={{ background: 'none', border: 'none', padding: 0, color: '#2E8B98', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}
            >
              choose a file
            </button>
          </div>
          <div style={{ fontSize: 10, color: '#ADB5BD' }}>
            PDF, PNG, JPG, HEIC or Word. Up to {fmtSize(MAX_BYTES)}.
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            data-testid="partner-doc-file"
            onChange={(e) => chooseFile(e.target.files && e.target.files[0])}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {localErr && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: 6, padding: '7px 9px', fontSize: 11, marginBottom: 10 }}>
          <AlertCircle size={12} style={{ verticalAlign: 'middle' }} /> {localErr}
        </div>
      )}

      {pending && (
        <div style={{ background: '#fff', border: '1px solid #DEE2E6', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: '#212529', marginBottom: 8 }}>
            <strong>{pending.file.name}</strong>{' '}
            <span style={{ color: '#6C757D' }}>· {fmtSize(pending.file.size)}</span>
          </div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>
            What kind of document is this?
          </label>
          <select
            value={pending.docType}
            aria-label="Document type"
            onChange={(e) => setPending((s) => ({ ...s, docType: e.target.value }))}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #CED4DA', borderRadius: 6, marginBottom: 8 }}
          >
            {TYPE_OPTIONS.map((v) => <option key={v} value={v}>{TYPE_LABELS[v]}</option>)}
          </select>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>
            Note <span style={{ fontWeight: 400, color: '#ADB5BD' }}>· optional</span>
          </label>
          <input
            type="text"
            value={pending.note}
            aria-label="Note"
            maxLength={MAX_NOTE}
            placeholder="e.g. countersigned copy, renewal due March"
            onChange={(e) => setPending((s) => ({ ...s, note: e.target.value }))}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #CED4DA', borderRadius: 6, marginBottom: 10 }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" disabled={busy} onClick={() => { setPending(null); setLocalErr(null); }}
              style={{ padding: '6px 12px', fontSize: 11, background: '#fff', border: '1px solid #CED4DA', borderRadius: 5, cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="button" disabled={busy} onClick={doUpload}
              style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, background: '#2E8B98', color: '#fff', border: '1px solid #2E8B98', borderRadius: 5, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
              {busy ? 'Saving…' : 'Save document'}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ fontSize: 12, color: '#6C757D', padding: 8 }}>
          <Loader2 size={13} className="spin" style={{ verticalAlign: 'middle' }} /> Loading documents…
        </div>
      )}

      {/*
          ⚠ SAY SO WHEN THE LIST IS CUT. The server asks for 201 and returns
          200, so it can tell "exactly 200" from "more than 200". Silently
          truncating meant a staff member searching for a document that exists
          would be told it does not.
      */}
      {!loading && truncated && (
        <div style={{ fontSize: 11, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 6, padding: '6px 9px', marginBottom: 8 }}>
          Showing the newest 200. This partner has more documents than are listed here.
        </div>
      )}

      {!loading && docs.length === 0 && (
        <div style={{ fontSize: 12, color: '#ADB5BD', padding: 8 }}>
          {showArchived ? 'Nothing archived.' : 'No documents on file yet.'}
        </div>
      )}

      {!loading && docs.map((d) => (
        <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '7px 0', borderBottom: '1px solid #F1F3F5' }}>
          <div style={{ minWidth: 220, flex: 1 }}>
            <div style={{ fontSize: 12, color: '#212529' }}>{d.fileName}</div>
            <div style={{ fontSize: 10, color: '#ADB5BD' }}>
              {TYPE_LABELS[d.docType] || d.docType} · {fmtSize(d.sizeBytes)} ·{' '}
              {d.uploadedByName || 'unknown'} · {fmtWhen(d.uploadedAt)}
              {d.note ? <> · {d.note}</> : null}
              {d.archivedAt ? <> · <em>archived {fmtWhen(d.archivedAt)}</em></> : null}
            </div>
          </div>
          <button type="button" disabled={busy} onClick={() => download(d)}
            style={{ padding: '4px 9px', fontSize: 11, background: '#fff', border: '1px solid #CED4DA', borderRadius: 5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Download size={11} /> Download
          </button>
          {canManage && (
            d.archivedAt ? (
              <button type="button" disabled={busy} onClick={() => setArchived(d, false)}
                style={{ padding: '4px 9px', fontSize: 11, background: '#fff', border: '1px solid #CED4DA', borderRadius: 5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <RotateCcw size={11} /> Restore
              </button>
            ) : (
              <button type="button" disabled={busy} onClick={() => setArchived(d, true)}
                title="Moves it to Archived. The file is kept — nothing is deleted."
                style={{ padding: '4px 9px', fontSize: 11, background: '#fff', border: '1px solid #CED4DA', borderRadius: 5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Archive size={11} /> Remove
              </button>
            )
          )}
        </div>
      ))}
    </div>
  );
}

export { TYPE_LABELS, TYPE_OPTIONS, MAX_BYTES };
